import type { SidebarMenuItem } from "@shared/types/sidebarMenu";
import type { UserButtonPage, UserMenuItem } from "@shared/types/user";
import { APP_DASHBOARD_PATH, APP_LAYOUT_ROUTE_NAME, APP_LAYOUT_ROUTE_PATH, MENU_HIDDEN, MENU_TYPE_BUTTON, MENU_TYPE_GROUP, MENU_TYPE_MENU } from "@shared/constants/menu";
import { APP_TARGET } from "@shared/utils/storage";
import { getRoutePageExtra, listRegisteredRouteNames } from "./routePageExtras";
import type { RouteComponent, RouteRecordRaw } from "vue-router";

function isCustomerWorkbench(): boolean {
    return APP_TARGET === "customer";
}

/** 客户端 /dashboard 是真实工作台；其它端仍只作登录桥接 */
function dashboardPageMeta(): { hidden: boolean; hideInMenu: boolean; affix: boolean; keepAliveName: string } {
    if (isCustomerWorkbench()) {
        return { hidden: false, hideInMenu: true, affix: true, keepAliveName: "Dashboard" };
    }
    return { hidden: true, hideInMenu: true, affix: false, keepAliveName: "Dashboard" };
}

export interface SplitUserMenusResult {
    /** 给侧栏：已去掉 hidden 节点；分组可嵌套。不依赖 routePages，避免没挂页的模块整组消失 */
    sidebar: SidebarMenuItem[];
    /** 给 Vue Router：只含能打开的叶子（含 hidden，方便深链） */
    pages: RouteRecordRaw[];
}

/**
 * 菜单 path 历史 kebab → 现行 camelCase。
 * 用于旧页签 / 书签跳转，避免改库 route 后访问旧 URL 落到 404。
 */
export const LEGACY_MENU_PATH_REDIRECTS: Readonly<Record<string, string>> = {
    "/store/amazon-vc": "/store/amazonVc",
    "/store/amazon-ad-account": "/store/amazonAd",
    "/store/walmart-dsv": "/store/walmartDsv",
};

/**
 * 把 user/info.menus 拆成侧栏树 + 叶子路由。
 * 侧栏按接口树展示；routePages 只决定能不能打开页面，不再决定侧栏出不出。
 */
export function splitUserMenus(menus: UserMenuItem[], buttonPages: UserButtonPage[] = []): SplitUserMenusResult {
    const usedNames = new Set<string>();
    const usedPaths = new Set<string>();
    const usedRegistryKeys = new Set<string>();
    const pages: RouteRecordRaw[] = [];

    const sidebar = mapSidebarNodes(menus ?? [], pages, usedNames, usedPaths, usedRegistryKeys);
    appendButtonPages(buttonPages ?? [], pages, usedNames, usedPaths, usedRegistryKeys);
    ensureDashboardPage(pages, usedNames, usedPaths, usedRegistryKeys);
    ensurePersonalCenterPage(pages, usedNames, usedPaths, usedRegistryKeys);
    ensureLegacyMenuPathRedirects(pages, usedNames, usedPaths);
    warnUnusedRoutePages(usedRegistryKeys);
    return { sidebar, pages };
}

function menuTypeOf(menu: UserMenuItem): number {
    return Number(menu.type);
}

function isMenuHidden(menu: UserMenuItem): boolean {
    return Number(menu.hidden) === MENU_HIDDEN;
}

/** /dashboard 只作登录桥接，不进侧栏 */
function isBridgeHomeMenu(path: string): boolean {
    return path === APP_DASHBOARD_PATH;
}

/** 分组，或历史数据里 type=菜单但下面还挂了菜单/分组（旧 mapOne 会当目录渲染） */
function isSidebarBranch(menu: UserMenuItem): boolean {
    if (menuTypeOf(menu) === MENU_TYPE_GROUP) return true;
    return (menu.children ?? []).some(child => {
        const type = menuTypeOf(child);
        return type === MENU_TYPE_GROUP || type === MENU_TYPE_MENU;
    });
}

function mapSidebarNodes(menus: UserMenuItem[], pages: RouteRecordRaw[], usedNames: Set<string>, usedPaths: Set<string>, usedRegistryKeys: Set<string>): SidebarMenuItem[] {
    const out: SidebarMenuItem[] = [];
    for (const menu of menus) {
        if (isSidebarBranch(menu)) {
            const children = mapSidebarNodes(menu.children ?? [], pages, usedNames, usedPaths, usedRegistryKeys);
            if (isMenuHidden(menu)) continue;
            if (!children.length) continue;
            out.push({
                id: String(menu.id),
                title: menu.title || "",
                type: "group",
                icon: menu.icon || undefined,
                selectIcon: menu.selectIcon || undefined,
                children,
            });
            continue;
        }

        if (menuTypeOf(menu) !== MENU_TYPE_MENU) continue;

        const page = tryBuildPage(menu, usedNames, usedPaths, usedRegistryKeys);
        if (page) pages.push(page);

        const path = page?.path || toUrlPath(menu.path || "");
        if (isMenuHidden(menu) || isBridgeHomeMenu(path)) continue;
        if (!path) continue;

        out.push({
            id: String(menu.id),
            title: menu.title || page?.meta?.title || "",
            type: "page",
            path,
            icon: menu.icon || undefined,
            selectIcon: menu.selectIcon || undefined,
        });
    }
    return out;
}

function tryBuildPage(menu: UserMenuItem, usedNames: Set<string>, usedPaths: Set<string>, usedRegistryKeys: Set<string>): RouteRecordRaw | null {
    const path = toUrlPath(menu.path || "");
    if (!path) {
        console.warn(`[routePages] 菜单缺少路由 path，已跳过: ${menu.title || menu.name || menu.id}`);
        return null;
    }
    if (usedPaths.has(path)) {
        console.warn(`[routePages] 路由 path 重复，已跳过: ${menu.title || menu.name} (${path})`);
        return null;
    }

    const fromNameKey = String(menu.name || "").trim();
    const fromPathKey = toRouteName("", path, menu.id);
    const fromName = getRoutePageExtra(fromNameKey);
    const extra = fromName ?? getRoutePageExtra(fromPathKey);
    if (!extra?.component) {
        console.warn(`[routePages] 未配置页面，已跳过: ${fromNameKey || fromPathKey} (${path})`);
        return null;
    }
    usedRegistryKeys.add(fromName ? fromNameKey : fromPathKey);

    const name = allocRouteName(menu.name, path, menu.id, usedNames);
    usedPaths.add(path);

    return {
        path,
        name,
        component: extra.component,
        meta: {
            title: menu.title || undefined,
            menuType: MENU_TYPE_MENU,
            iconDefault: menu.icon || undefined,
            iconActive: menu.selectIcon || undefined,
            hidden: isMenuHidden(menu) || (path === APP_DASHBOARD_PATH && !isCustomerWorkbench()),
            noCache: menu.keepalive === 0,
            ...(path === APP_DASHBOARD_PATH ? dashboardPageMeta() : {}),
            ...(extra.noPin ? { noPin: true } : {}),
            ...(extra.keepAliveName ? { keepAliveName: extra.keepAliveName } : {}),
        },
    };
}

function appendButtonPages(buttonPages: UserButtonPage[], pages: RouteRecordRaw[], usedNames: Set<string>, usedPaths: Set<string>, usedRegistryKeys: Set<string>): void {
    for (const button of buttonPages) {
        const page = tryBuildButtonPage(button, usedNames, usedPaths, usedRegistryKeys);
        if (page) pages.push(page);
    }
}

function tryBuildButtonPage(button: UserButtonPage, usedNames: Set<string>, usedPaths: Set<string>, usedRegistryKeys: Set<string>): RouteRecordRaw | null {
    const path = toUrlPath(button.path || "");
    const fromNameKey = String(button.name || "").trim();
    if (!path || !fromNameKey) {
        console.warn(`[routePages] 按钮页缺少路由，已跳过: ${button.title || button.id}`);
        return null;
    }
    if (usedPaths.has(path)) {
        console.warn(`[routePages] 按钮路由 path 重复，已跳过: ${button.title || fromNameKey} (${path})`);
        return null;
    }

    const extra = getRoutePageExtra(fromNameKey) ?? getRoutePageExtra(toRouteName("", path, button.id));
    if (!extra?.component) {
        console.warn(`[routePages] 未配置按钮页面，已跳过: ${fromNameKey} (${path})`);
        return null;
    }
    usedRegistryKeys.add(getRoutePageExtra(fromNameKey) ? fromNameKey : toRouteName("", path, button.id));

    const name = allocRouteName(fromNameKey, path, button.id, usedNames);
    usedPaths.add(path);

    return {
        path,
        name,
        component: extra.component,
        meta: {
            title: button.title || undefined,
            menuType: MENU_TYPE_BUTTON,
            hidden: false,
            hideInMenu: true,
            noCache: button.keepalive === 0,
            ...(button.code ? { permissionCode: button.code } : {}),
            ...(extra.noPin ? { noPin: true } : {}),
            ...(extra.keepAliveName ? { keepAliveName: extra.keepAliveName } : {}),
        },
    };
}

function ensureDashboardPage(pages: RouteRecordRaw[], usedNames: Set<string>, usedPaths: Set<string>, usedRegistryKeys: Set<string>): void {
    if (usedPaths.has(APP_DASHBOARD_PATH)) {
        const existing = pages.find(p => p.path === APP_DASHBOARD_PATH);
        if (existing) {
            existing.meta = {
                ...(existing.meta || {}),
                ...dashboardPageMeta(),
                title: existing.meta?.title || "首页",
            };
        }
        return;
    }
    const extra = getRoutePageExtra("Dashboard");
    if (!extra?.component) return;
    usedPaths.add(APP_DASHBOARD_PATH);
    usedNames.add("Dashboard");
    usedRegistryKeys.add("Dashboard");
    pages.unshift({
        path: APP_DASHBOARD_PATH,
        name: "Dashboard",
        component: extra.component,
        meta: {
            title: "首页",
            ...dashboardPageMeta(),
            ...(extra.keepAliveName ? { keepAliveName: extra.keepAliveName } : {}),
        },
    });
}

function ensurePersonalCenterPage(pages: RouteRecordRaw[], usedNames: Set<string>, usedPaths: Set<string>, usedRegistryKeys: Set<string>): void {
    if (usedPaths.has("/system/personalCenter")) return;
    const extra = getRoutePageExtra("SystemPersonalCenter");
    if (!extra?.component) return;
    usedPaths.add("/system/personalCenter");
    usedRegistryKeys.add("SystemPersonalCenter");
    const name = allocRouteName("SystemPersonalCenter", "/system/personalCenter", "personalCenter", usedNames);
    pages.push({
        path: "/system/personalCenter",
        name,
        component: extra.component,
        meta: {
            title: "个人中心",
            hideInMenu: true,
            ...(extra.keepAliveName ? { keepAliveName: extra.keepAliveName } : {}),
        },
    });
}

/** 把已知旧 path 注册为隐藏 redirect，仅当新 path 已在菜单树中 */
function ensureLegacyMenuPathRedirects(pages: RouteRecordRaw[], usedNames: Set<string>, usedPaths: Set<string>): void {
    for (const [from, to] of Object.entries(LEGACY_MENU_PATH_REDIRECTS)) {
        if (!usedPaths.has(to) || usedPaths.has(from)) continue;
        const name = allocRouteName(`LegacyRedirect${from.replace(/\W+/g, "")}`, from, from, usedNames);
        usedPaths.add(from);
        pages.push({
            path: from,
            name,
            redirect: to,
            meta: { hidden: true, hideInMenu: true },
        });
    }
}

/** 表有、接口没有：方便发现登记了却从未挂上的页。站内信走 guaranteedRoutes，先不算进这份。 */
function warnUnusedRoutePages(usedRegistryKeys: Set<string>): void {
    const unused = listRegisteredRouteNames().filter(name => {
        if (usedRegistryKeys.has(name)) return false;
        if (name.startsWith("InSiteMessage")) return false;
        return true;
    });
    if (!unused.length) return;
    console.warn(`[routePages] 已登记但菜单未下发: ${unused.join(", ")}`);
}

/** 组件 path → 路由 URL：去 .vue、去尾部 /index、补前导 / */
export function toUrlPath(raw: string): string {
    let p = String(raw || "")
        .trim()
        .replace(/\\/g, "/")
        .replace(/\.vue$/i, "");
    if (!p) return "";
    p = p.replace(/\/index$/i, "");
    if (!p.startsWith("/")) p = `/${p}`;
    p = p.replace(/\/+/g, "/");
    if (p.length > 1 && p.endsWith("/")) p = p.slice(0, -1);
    return p;
}

/**
 * 分配唯一 route name：后台 routeName 优先，撞名时改用 path 推导，避免 keep-alive 串缓存。
 */
function allocRouteName(rawName: string | undefined, path: string, id: number | string, usedNames: Set<string>): string {
    const preferred = toRouteName(rawName, path, id);
    const fromPath = toRouteName("", path, id);
    const candidates = preferred === fromPath ? [preferred] : [preferred, fromPath];
    for (const candidate of candidates) {
        if (candidate && !usedNames.has(candidate)) {
            usedNames.add(candidate);
            return candidate;
        }
    }
    const base = fromPath || preferred || `Menu${id}`;
    let i = 2;
    while (usedNames.has(`${base}${i}`)) i += 1;
    const unique = `${base}${i}`;
    usedNames.add(unique);
    return unique;
}

/** Vue Router name 不能含 /；由 path 生成 PascalCase */
export function toRouteName(rawName: string | undefined, path: string, id: number | string): string {
    const name = String(rawName || "").trim();
    if (name && !name.includes("/")) return name;

    const fromPath = path
        .replace(/^\//, "")
        .split("/")
        .filter(Boolean)
        .map(seg =>
            seg
                .split("-")
                .filter(Boolean)
                .map(s => s.charAt(0).toUpperCase() + s.slice(1))
                .join(""),
        )
        .join("");

    return fromPath || `Menu${id}`;
}

/** `/product/goods` → `product/goods`，作为 Layout(`/`) 的相对子路由，浏览器 URL 仍是 /product/goods */
function toLayoutChildPath(path: string): string {
    const p = String(path || "").trim();
    if (!p || p === "/") return "";
    return p.startsWith("/") ? p.slice(1) : p;
}

/**
 * 所有业务页挂到同一 Layout。
 * 子路由必须用相对 path：父级若写 /__layout 且子级写绝对 /dashboard，登录后 replace('/dashboard')
 * 对不上叶子，会 404 / 导航失败，表现成「登录不上」。
 */
export function buildLayoutRoute(pages: RouteRecordRaw[], layout: RouteComponent): RouteRecordRaw {
    return {
        path: APP_LAYOUT_ROUTE_PATH,
        name: APP_LAYOUT_ROUTE_NAME,
        component: layout,
        meta: { hidden: true },
        children: pages.map(page => ({
            ...page,
            path: toLayoutChildPath(String(page.path || "")),
        })),
    };
}
